import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import DownloadTableButtons from "../../components/DownloadTableButtons";
import { rightAction } from "../../redux/actions";
import cnt from "../../services/definedConstant";
import { BOM } from "../../services/definedMethod";
import MainLayout from "../../components/layouts/enhancement/MainLayout";
import TopContent from "../../components/layouts/enhancement/TopContent";
import { apiConfig, labelConfig, staticConfig } from "./serviceProvider.config";
import { handleFlgStatus } from "../../components/facilities/AccountFacility";
import CustomPagination from "../../components/CustomPagination";

const { Text } = Typography;

const columns = [
  {
    title: labelConfig.agentId,
    dataIndex: "agentId",
    sorter: (a, b) => a.agentId.localeCompare(b.agentId),
  },
  {
    title: labelConfig.name,
    dataIndex: "name",
    sorter: (a, b) => a.name.localeCompare(b.name),
  },
  {
    title: labelConfig.agentType,
    dataIndex: "agentType",
    sorter: (a, b) => a.agentType.localeCompare(b.agentType),
  },
  {
    title: labelConfig.mobilePhone,
    dataIndex: "mobileNo",
  },
  {
    title: labelConfig.branch,
    dataIndex: "branch",
    sorter: (a, b) => a.branch.localeCompare(b.branch),
  },
  {
    title: labelConfig.relationshipManager,
    dataIndex: "staff",
  },
  {
    title: labelConfig.status,
    dataIndex: "status",
    render: (_, record) => (
      <Text>{handleFlgStatus(record?.status, {}, true)}</Text>
    ),
  },
];

const ManageServiceProvider = (props) => {
  const navigate = useNavigate();
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData;

  const [customerList, setCustomerList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearch, setIsSearch] = useState(false);

  const [useForm] = Form.useForm();
  const pageSize = 10;
  const dispatch = useDispatch();

  useEffect(() => {
    BOM.HasPermission("READ_SAVINGSACCOUNT") && getCustomerListAction();
    BOM.HasPermission("CREATE_SAVINGSACCOUNT") &&
      rightAction(cnt.RIGHT_ADD_BTN);
  }, []);

  // Status is filtered server-side so totalFilteredRecords matches the rows shown
  const getCustomerListAction = (offset = 0, status = selectedStatus) => {
    setIsLoading(true);
    setIsSearch(false);

    const params = {
      limit: pageSize,
      offset,
      orderBy: "id",
      sortOrder: "DESC",
      agentMerchantOnly: true,
    };

    // "status" is the "All Status" option
    if (status && status !== "status") {
      params.status = status;
    }

    const body = {
      session,
      params,
    };

    BOM.FetchReqAction(body, apiConfig.Clients, (err, res) => {
      if (err) {
        setIsLoading(false);
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
        return;
      }

      setTotalItems(res?.totalFilteredRecords);

      const tempArray = res.pageItems.map((element) => {
        const statusValue = element?.status ? element.status.value : "-";
        return {
          key: element?.id,
          id: element?.id,
          name: element?.displayName || "-",
          clientno: element?.accountNo || "-",
          agentId: element?.agentMerchantData?.agentCode || "-",
          agentType: element?.agentMerchantData?.agentType || "-",
          mobileNo: element?.mobileNo || "-",
          status:
            statusValue?.toLowerCase() === "submitted and pending approval"
              ? "Pending"
              : statusValue,
          branch: element?.officeName || "-",
          staff: element?.staffName || "-",
          branchId: element?.officeId || "-",
        };
      });

      setCustomerList(tempArray);
      setIsLoading(false);
    });
  };

  const onSearch = (formValue) => {
    setIsLoading(true);
    setIsSearch(true);

    if (!formValue.searchKey.trim()) {
      BOM.AlertMsg(cnt.WARNING, "Search parameter is required");
      setIsLoading(false);
      return;
    }

    const body = {
      session,
      params: {
        exactMatch: false,
        query: formValue.searchKey.trim(),
        resource: "clients,clientIdentifiers",
      },
    };

    BOM.FetchReqAction(body, apiConfig.Search, (err, res) => {
      if (err) {
        setIsLoading(false);
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
        return;
      } else {
        const tempArray = res.map((element) => ({
          key: element?.entityId,
          id: element?.entityId,
          name: element?.entityName || "-",
          clientno: element?.accountNo || "-",
          agentId: element?.entityCode || "-",
          agentType: element?.agentType || "-",
          mobileNo: element?.entityMobileNo || "-",
          status: element?.entityStatus?.value || "-",
          branch: element?.parentName || "-",
          staff: element?.staffName || "-",
          branchId: element?.officeId || "-",
        }));

        setCustomerList(tempArray);
        setIsLoading(false);
      }
    });
  };

  const onRefresh = (offset = 0) => {
    useForm.resetFields();
    getCustomerListAction(offset);
    setCurrentPage(1);
  };

  const onSelectItemAction = (selectedKeys, selectedRows) => {
    setSelectedRowKeys(selectedKeys);
    if (!selectedKeys.length || selectedKeys.length !== 1) {
      setSelectedRow(null);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });
      BOM.HasPermission("CREATE_SERVICEPROVIDER") &&
        dispatch({ type: cnt.RIGHT_ADD_BTN });
      return;
    }

    setSelectedRow(selectedRows[0]);

    [
      BOM.HasPermission("READ_SERVICEPROVIDER") && cnt.RIGHT_DETAIL_BTN,
      BOM.HasPermission("UPDATE_SERVICEPROVIDER") && cnt.RIGHT_EDIT_BTN,
    ].forEach((item) => item && dispatch({ type: item }));
  };

  function handleOnView() {
    if (rightBtn.detailBtn.toLowerCase() === "primary") {
      navigate("/detail-service-provider", { state: selectedRow });
    }
  }

  function handleOnCreate() {
    if (rightBtn.addBtn.toLowerCase() === "primary") {
      navigate("/new-service-provider", { ofObj: selectedRow });
    }
  }

  function handleOnEdit() {
    if (rightBtn.editBtn.toLowerCase() === "primary") {
      navigate("/edit-service-provider", { state: selectedRow });
    }
  }

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    getCustomerListAction(0, status);
  };

  return (
    <MainLayout
      isEnableRightSidebar={true}
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
      pageItems={staticConfig.manageBreadcrumbItems}
    >
      <TopContent
        pageTitle={labelConfig.ManageServiceProvider}
        createPageTitle={labelConfig.serviceProviderListing}
      />

      <Card
        title={
          <>
            Agent List{" "}
            <Button
              type="primary"
              shape="round"
              size="small"
              className="of-primaryBtnColor"
              onClick={() => onRefresh()}
              icon={<ReloadOutlined />}
              loading={isLoading}
            >
              {labelConfig.refresh}
            </Button>
          </>
        }
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
          marginTop: 20,
        }}
      >
        <Spin spinning={isLoading} size="large">
          <Row style={{ marginBottom: 20 }}>
            <Form form={useForm} onFinish={onSearch}>
              <Space.Compact
                style={{
                  width: "100%",
                }}
              >
                <Form.Item
                  name="searchKey"
                  // rules={[
                  //   { required: true, message: BOM.CheckReqFld("This field ") },
                  // ]}
                >
                  <Input
                    placeholder="Search by: Name / ID / Mobile Number"
                    style={{ width: 400, borderRadius: "10px 0 0 10px" }}
                    allowClear
                  />
                </Form.Item>
                <Button
                  type="primary"
                  className="of-primaryBtnColor"
                  htmlType="submit"
                  style={{ borderRadius: "0px 5px 5px 0px" }}
                >
                  <SearchOutlined />
                </Button>
                <div style={{ marginLeft: "20px" }}>
                  <Select
                    value={selectedStatus}
                    options={staticConfig.customerStatusOptions}
                    onChange={handleStatusChange}
                    placeholder="Select status"
                    dropdownStyle={{ width: "250px" }}
                    className="interbank-table-select"
                  />
                </div>
              </Space.Compact>
            </Form>
            <DownloadTableButtons
              downloadData={customerList}
              columns={columns}
              fileName={"Service Provider List"}
            />
          </Row>

          <Table
            className="bond-table"
            rowSelection={{
              onChange: onSelectItemAction,
              hideSelectAll: true,
            }}
            columns={columns}
            dataSource={customerList}
            pagination={false}
            scroll={{ x: 800 }}
          />

          {!isSearch && (
            <CustomPagination
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={(page, offset) => {
                setCurrentPage(page);
                getCustomerListAction(offset);
              }}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          )}
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = { rightAction };

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ManageServiceProvider);
